'use client';

import { useEffect, useRef, useState } from 'react';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { Icon } from '@benzinga/core-ui';
import type { Ticker } from '@benzinga/session';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import type { Section, SectionLink, PageContext } from '../../entities';
import type { PremiumServiceAccess, PremiumServiceKey } from '../../premiumServiceLinks';
import { sectionMenus as defaultSectionMenus } from '../../section-menu-data';
import { SectionMenuButton } from './SectionMenuButton';
import { SectionMenu } from './SectionMenu';
import { NavLogo } from './NavLogo';
import { PremiumServicesDropdown } from './PremiumServicesDropdown';
import { TickerSearch } from './TickerSearch';
import styles from './SiteNav.module.scss';

export interface SectionNavProps {
  sections?: Section[];
  variant?: 'default' | 'dark';
  premiumAccess: PremiumServiceAccess;
  pageContext?: PageContext;
  initialTrendingTickers?: Ticker[];
  initialTrendingTopics?: TrendingTopic[];
  onSectionOpen?: (section: Section) => void;
  onSectionLinkClick?: (section: Section, link: SectionLink) => void;
  onPremiumClick?: (service: PremiumServiceKey) => void;
  hideTickerSearch?: boolean;
  hidePremium?: boolean;
  mobileMenuOpen?: boolean;
  onOpenMobileMenu?: () => void;
}

export const SectionNav = ({
  sections = defaultSectionMenus,
  variant = 'default',
  premiumAccess,
  pageContext,
  initialTrendingTickers,
  initialTrendingTopics,
  mobileMenuOpen,
  onSectionOpen,
  onSectionLinkClick,
  onPremiumClick,
  hideTickerSearch = false,
  hidePremium = false,
  onOpenMobileMenu,
}: SectionNavProps) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSection = openKey ? sections.find(section => section.key === openKey) ?? null : null;
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const cancelOpen = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      if (openTimer.current) clearTimeout(openTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!openKey) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenKey(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openKey]);

  const closeSearchDropdown = () => {
    document.getElementById('searchbar-input')?.blur();
  };

  const handleSearchActivate = () => {
    cancelOpen();
    cancelClose();
    setOpenKey(null);
  };

  const handleOpen = (section: Section) => {
    cancelClose();
    cancelOpen();
    const open = () => {
      closeSearchDropdown();
      setOpenKey(section.key);
      onSectionOpen?.(section);
    };
    if (openKey) open();
    else openTimer.current = setTimeout(open, 120);
  };
  const closeSections = () => {
    cancelOpen();
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenKey(null), 150);
  };

  return (
    <div className="relative border-b border-[var(--hubnav-line)]" onMouseLeave={closeSections}>
      <div className="relative mx-auto flex h-16 w-full max-w-[var(--hubnav-max-width)] items-center gap-1.5 px-[var(--hubnav-gutter)]">
        <span className="inline-flex w-[168px] flex-none items-center max-[1400px]:w-[152px]" onMouseEnter={closeSections}>
          <NavLogo variant={variant} />
        </span>

        <nav className="ml-3 flex items-center self-stretch max-[1080px]:hidden" aria-label="Primary">
          {sections.map(section => (
            <SectionMenuButton
              key={section.key}
              section={section}
              open={section.key === openKey}
              onOpen={() => handleOpen(section)}
            />
          ))}
        </nav>

        {!hideTickerSearch || !hidePremium ? (
          <div className="ml-auto flex items-center gap-2 max-[1080px]:hidden" onMouseEnter={closeSections}>
            {!hideTickerSearch ? (
              <TickerSearch
                dropdownSuppressed={!!openKey}
                initialTrendingTickers={initialTrendingTickers}
                initialTrendingTopics={initialTrendingTopics}
                onActivate={handleSearchActivate}
              />
            ) : null}
            {!hidePremium ? (
              <span className="inline-flex min-[1760px]:hidden">
                <PremiumServicesDropdown
                  access={premiumAccess}
                  label="Premium"
                  variant={variant}
                  onLinkClick={onPremiumClick}
                />
              </span>
            ) : null}
          </div>
        ) : null}

        <button
          className="ml-auto hidden h-10 items-center gap-2 rounded-[9px] border border-[var(--hubnav-line)] bg-transparent px-3 font-[inherit] text-sm font-extrabold text-[var(--hubnav-fg)] max-[1080px]:inline-flex"
          type="button"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          onClick={onOpenMobileMenu}
        >
          <Icon icon={mobileMenuOpen ? faXmark : faBars} />
          <span className="leading-none">{mobileMenuOpen ? 'Close' : 'Menu'}</span>
        </button>

        {activeSection ? (
          <div
            className={`${styles.panelIn} absolute left-[var(--hubnav-gutter)] top-[calc(100%+8px)] z-[60] w-max max-w-[calc(100%-2*var(--hubnav-gutter))] overflow-hidden rounded-xl bg-[var(--hubnav-panel-bg)] text-[var(--hubnav-fg)] before:absolute before:inset-x-0 before:-top-3 before:h-3 before:content-['']`}
            role="region"
            aria-label={activeSection.label}
            onMouseEnter={cancelClose}
          >
            <SectionMenu section={activeSection} pageContext={pageContext} onLinkClick={onSectionLinkClick} />
          </div>
        ) : null}
      </div>

      {!hidePremium ? (
        <span
          className="absolute top-0 hidden h-16 items-center min-[1760px]:inline-flex"
          style={{ left: 'calc(50% + var(--hubnav-max-width) / 2 + 12px)' }}
          onMouseEnter={closeSections}
        >
          <PremiumServicesDropdown
            access={premiumAccess}
            label="Premium Services"
            variant={variant}
            onLinkClick={onPremiumClick}
          />
        </span>
      ) : null}
    </div>
  );
};
